import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  Banknote,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Download,
  FileText,
  Landmark,
  ListChecks,
  Loader2,
  Printer,
  Receipt,
  ReceiptText,
  Sparkles,
  Wallet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './button';
import { DateTimePicker } from './DatePicker';
import { InfoHint } from './InfoHint';
import { useHospitalTimezone } from '../../hooks/useHospitalTimezone';
import { datetimeLocalToISOInTimeZone, toDatetimeLocalValueInTimeZone } from '../../lib/timezones';
import {
  useCreateInvoice,
  useRecordPayment,
  useUnbilledCharges,
  useAdmissionBillingStatus,
  useUpdateAdmissionChargeQuantity,
} from '../../services/httpServices/queries';
import { QuantityStepper } from './quantity-stepper';
import { usePermissions } from '../../hooks/usePermissions';
import { serviceQuantityBounds } from '../../utils/servicePricing';
import { billingService } from '../../services/httpServices/billingService';
import { ipdService } from '../../services/httpServices/ipdService';
import { printInvoicePdf } from '../../utils/printDocument';
import { money, round2, sanitizeDecimalInput } from '../../pages/financial/invoice-discount-utils';
import { useAppSelector } from '../../redux/store/hooks';
import type { Admission } from '../../services/httpServices/ipdService';

type Step = 'invoice' | 'invoice-success';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'card', label: 'Card', icon: CircleDollarSign },
  { value: 'upi', label: 'UPI', icon: Wallet },
  { value: 'bank_transfer', label: 'Bank', icon: Landmark },
];

const inputClass =
  'w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary/40 transition';

interface AdmissionInvoiceModalProps {
  open: boolean;
  /** The admission the invoice is raised for. */
  admission: Admission | null;
  /** Display name for the admitted patient. */
  patientName?: string;
  /**
   * `created` — shown right after an admission is saved.
   * `existing` — opened from an admission detail page, adds "full pay all" and
   * offers Discharge / Go to List once the bill is cleared.
   */
  variant?: 'created' | 'existing';
  /** Close/skip the modal (navigates back to the IPD list). */
  onDone: () => void;
  /** Open the new admission's detail page (created variant). */
  onViewAdmission?: () => void;
  /** Open the full invoices list. */
  onViewInvoices: () => void;
  /** Existing variant: discharge the admission after its bill is cleared. */
  onDischarge?: () => void;
  /** Existing variant: return to the admissions list. */
  onGoToList?: () => void;
  /** True while the discharge request is in flight. */
  discharging?: boolean;
}

/**
 * Post-admission billing dialog. Shown right after an admission is saved so the
 * counter can raise the first invoice and optionally collect payment in place.
 */
export function AdmissionInvoiceModal({
  open,
  admission,
  patientName,
  variant = 'created',
  onDone,
  onViewAdmission,
  onViewInvoices,
  onDischarge,
  onGoToList,
  discharging = false,
}: AdmissionInvoiceModalProps) {
  const { user } = useAppSelector((state) => state.auth);
  const { hasAnyPermission, hasPermission } = usePermissions();
  const createInvoice = useCreateInvoice();
  const recordPayment = useRecordPayment();
  const updateChargeQuantity = useUpdateAdmissionChargeQuantity();
  // Same rule as the admission page: the existing IPD update right is enough to
  // adjust a line before it is invoiced, and `ipd:charge:update` grants it on
  // its own.
  const canEditChargeQuantity = hasAnyPermission([
    'ipd:charge:update',
    'ipd:update',
  ]);
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, number>>({});
  const [savingChargeId, setSavingChargeId] = useState<string | null>(null);

  const isExisting = variant === 'existing';

  const [step, setStep] = useState<Step>('invoice');
  const [error, setError] = useState<string | null>(null);

  // ─── Invoice state ───
  const [selectedChargeIds, setSelectedChargeIds] = useState<string[]>([]);
  const [autoSelected, setAutoSelected] = useState(false);
  const [discountValue, setDiscountValue] = useState('');
  const [discountType, setDiscountType] = useState<'PERCENT' | 'FLAT'>('PERCENT');
  const [notes, setNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [createdInvoice, setCreatedInvoice] = useState<any>(null);
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ── Backdating ──
  // An IPD bill belongs to the stay, so a role holding `billing:invoice:backdate`
  // may date it on the admission day instead of "now". Roles without the
  // capability never send a date and the server stamps the save instant.
  const canBackdateInvoice = hasPermission('billing:invoice:backdate');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceDateError, setInvoiceDateError] = useState<string | undefined>();
  const { timezone: hospitalTimezone } = useHospitalTimezone();
  // Existing variant only: set when a "full pay all" run leaves nothing owing.
  const [billCleared, setBillCleared] = useState(false);
  // Balance that would be carried if the patient were discharged now. Only set
  // when every charge is invoiced — otherwise discharge is blocked outright.
  const [carriedDue, setCarriedDue] = useState(0);
  const [settledEarlierCount, setSettledEarlierCount] = useState(0);
  const [fullPaying, setFullPaying] = useState(false);

  const { data: unbilledData, isLoading: chargesLoading } = useUnbilledCharges(
    open ? admission?.visitId : undefined,
  );
  const unbilledCharges = useMemo(() => unbilledData?.data || [], [unbilledData]);

  // Every invoice on the visit (with its live due) — only needed on the detail
  // page, where staff can settle the whole bill in one action.
  const { data: billingStatusData, isLoading: billingStatusLoading } =
    useAdmissionBillingStatus(isExisting && open ? admission?.id : undefined);
  const billingStatus = billingStatusData?.data;
  const openInvoices = useMemo(
    () =>
      (billingStatus?.invoices || []).filter(
        (invoice) => Number(invoice.dueAmount) > 0,
      ),
    [billingStatus],
  );
  const earlierDue = billingStatus?.outstandingDue ?? 0;

  // Discharge is possible once nothing is unbilled; any balance is carried.
  const canDischargeWithDue = Boolean(billingStatus?.canDischarge);
  const canDischargeNow = billCleared || carriedDue > 0;

  // Keep the carried balance in step with the live bill (e.g. after a payment).
  useEffect(() => {
    if (!billingStatus) return;
    setBillCleared(Boolean(billingStatus.isCleared));
    setCarriedDue(
      billingStatus.canDischarge ? Number(billingStatus.outstandingDue) || 0 : 0,
    );
  }, [billingStatus]);

  // Reset every time a new admission opens the dialog.
  useEffect(() => {
    if (!open) return;
    setStep('invoice');
    setError(null);
    setSelectedChargeIds([]);
    setAutoSelected(false);
    setDiscountValue('');
    setDiscountType('PERCENT');
    setNotes('');
    setPaymentAmount('');
    setPaymentMode('cash');
    setPaymentReference('');
    setCreatedInvoice(null);
    setBillCleared(false);
    setCarriedDue(0);
    setSettledEarlierCount(0);
    setQuantityDrafts({});
    setSavingChargeId(null);
    // An IPD bill defaults to the stay's admission day; the counter can move it.
    const stored = admission?.admittedAt || (admission as any)?.admissionDate;
    setInvoiceDate(
      stored
        ? toDatetimeLocalValueInTimeZone(new Date(stored), hospitalTimezone)
        : toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone),
    );
    setInvoiceDateError(undefined);
    // Intentionally keyed on the admission id (not the object): the list hands
    // the modal a fresh row object on every refetch, and re-running this reset
    // on each parent render would wipe the form while the counter is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, admission?.id, hospitalTimezone]);

  // Pre-select every unbilled charge once the charges load, so the common
  // "bill everything" case is a single click.
  useEffect(() => {
    if (autoSelected || unbilledCharges.length === 0) return;
    setSelectedChargeIds(unbilledCharges.map((charge: any) => charge.id));
    setAutoSelected(true);
  }, [autoSelected, unbilledCharges]);

  // Escape closes the dialog (unless a request is in flight).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (createInvoice.isPending || recordPayment.isPending || fullPaying || discharging)
        return;
      onDone();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    open,
    createInvoice.isPending,
    recordPayment.isPending,
    fullPaying,
    discharging,
    onDone,
  ]);

  const selectedCharges = useMemo(
    () => unbilledCharges.filter((charge: any) => selectedChargeIds.includes(charge.id)),
    [unbilledCharges, selectedChargeIds],
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    for (const charge of selectedCharges) {
      subtotal += Number(charge.amount || 0);
      tax += Number(charge.tax || 0);
    }
    const subtotalR = round2(subtotal);
    const taxR = round2(tax);
    const gross = round2(subtotalR + taxR);

    const parsed = parseFloat(discountValue);
    let discount = 0;
    if (Number.isFinite(parsed) && parsed > 0) {
      discount =
        discountType === 'PERCENT'
          ? round2((subtotalR * Math.min(parsed, 100)) / 100)
          : round2(parsed);
    }
    const appliedDiscount = round2(Math.min(discount, gross));

    return {
      count: selectedCharges.length,
      subtotal: subtotalR,
      tax: taxR,
      gross,
      discount: appliedDiscount,
      total: round2(Math.max(gross - appliedDiscount, 0)),
    };
  }, [selectedCharges, discountValue, discountType]);

  const paymentValue = useMemo(() => {
    const parsed = parseFloat(paymentAmount);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return round2(Math.min(parsed, totals.total));
  }, [paymentAmount, totals.total]);

  /** Unbilled charges selected on this invoice plus every earlier open invoice. */
  const fullPayTotal = useMemo(
    () =>
      round2(
        (selectedChargeIds.length > 0 ? totals.total : 0) +
          (isExisting ? Number(earlierDue) : 0),
      ),
    [selectedChargeIds.length, totals.total, isExisting, earlierDue],
  );
  const canFullPayAll =
    isExisting &&
    !chargesLoading &&
    !billingStatusLoading &&
    fullPayTotal > 0 &&
    (selectedChargeIds.length > 0 || openInvoices.length > 0);

  const allSelected =
    unbilledCharges.length > 0 && selectedChargeIds.length === unbilledCharges.length;

  const toggleCharge = (id: string) => {
    setSelectedChargeIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    setSelectedChargeIds(allSelected ? [] : unbilledCharges.map((charge: any) => charge.id));
  };

  /**
   * Re-prices one unbilled line before it goes on the invoice. The backend
   * applies the catalog's minimum units / rounding, so the draft is dropped
   * once it answers and the row shows the resolved quantity.
   */
  const handleChargeQuantityChange = async (
    charge: any,
    nextQuantity: number,
  ) => {
    if (!admission) return;
    const previousDraft = quantityDrafts[charge.id];
    setQuantityDrafts((prev) => ({ ...prev, [charge.id]: nextQuantity }));
    setSavingChargeId(charge.id);
    try {
      await updateChargeQuantity.mutateAsync({
        admissionId: admission.id,
        chargeId: charge.id,
        quantity: nextQuantity,
      });
      setQuantityDrafts((prev) => {
        const next = { ...prev };
        delete next[charge.id];
        return next;
      });
    } catch (err: any) {
      setQuantityDrafts((prev) => {
        const next = { ...prev };
        if (previousDraft === undefined) delete next[charge.id];
        else next[charge.id] = previousDraft;
        return next;
      });
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not change the quantity.',
      );
    } finally {
      setSavingChargeId(null);
    }
  };

  const handleDiscountChange = (raw: string) => {
    setDiscountValue(sanitizeDecimalInput(raw));
  };

  /** Builds the create-invoice payload from the selected charges and discount. */
  const buildInvoicePayload = () => {
    if (!admission) throw new Error('Admission is required to create an invoice.');
    const parsedDiscount = parseFloat(discountValue);
    const hasDiscount =
      Boolean(discountValue) && Number.isFinite(parsedDiscount) && parsedDiscount > 0;
    const safePercent =
      totals.subtotal > 0
        ? Math.min(parsedDiscount || 0, round2((totals.gross / totals.subtotal) * 100))
        : 0;

    return {
      patientId: admission.patientId,
      visitId: admission.visitId,
      chargeEventIds: selectedChargeIds,
      notes: notes.trim() || undefined,
      issuedById: user?.id,
      // Omitted for roles without the backdate permission.
      ...(canBackdateInvoice && invoiceDate
        ? { invoiceDate: datetimeLocalToISOInTimeZone(invoiceDate, hospitalTimezone) }
        : {}),
      ...(hasDiscount
        ? discountType === 'PERCENT'
          ? { discountPercent: safePercent }
          : { discount: Math.min(parsedDiscount, totals.gross) }
        : {}),
    };
  };

  const handleCreateInvoice = async () => {
    if (!admission) return;
    setError(null);
    if (selectedChargeIds.length === 0) {
      setError('Select at least one charge to include in the invoice.');
      return;
    }

    try {
      const response = await createInvoice.mutateAsync(buildInvoicePayload());

      const created = (response as any)?.data;
      let paidNow = Number(created?.paidAmount || 0);
      const totalNow = Number(created?.total || 0);

      if (created?.id && paymentValue > 0) {
        try {
          await recordPayment.mutateAsync({
            invoiceId: created.id,
            amount: Math.min(paymentValue, totalNow || paymentValue),
            paymentMode,
            referenceNumber: paymentReference.trim() || undefined,
            receivedBy: user?.id,
          });
          paidNow = round2(paidNow + Math.min(paymentValue, totalNow || paymentValue));
        } catch (paymentError: any) {
          toast.error(
            `Invoice created, but the payment could not be recorded: ${
              paymentError?.message || 'unknown error'
            }`,
          );
        }
      }

      setCreatedInvoice({
        ...(created || {}),
        paidAmount: paidNow,
        dueAmount: round2(Math.max(totalNow - paidNow, 0)),
      });
      setSettledEarlierCount(0);

      // On an existing admission, a single fully-paid invoice may already clear
      // the whole visit — check the live bill so Discharge can be offered.
      if (isExisting) {
        try {
          const fresh = await ipdService.getAdmissionBillingStatus(admission.id);
          setBillCleared(Boolean(fresh.data?.isCleared));
          setCarriedDue(
            fresh.data?.canDischarge
              ? Number(fresh.data?.outstandingDue) || 0
              : 0,
          );
        } catch {
          setBillCleared(false);
          setCarriedDue(0);
        }
      } else {
        setBillCleared(false);
        setCarriedDue(0);
      }
      setStep('invoice-success');
    } catch (invoiceError: any) {
      setError(invoiceError?.message || 'Failed to create the invoice.');
    }
  };

  /**
   * Existing admission only: raise the invoice for the selected unbilled
   * charges (when there are any) and settle it *and* every earlier open invoice
   * in full, then report whether the patient is clear to discharge.
   */
  const handleFullPayAll = async () => {
    if (!admission) return;
    setError(null);
    if (selectedChargeIds.length === 0 && openInvoices.length === 0) {
      setError('There is nothing left to settle for this admission.');
      return;
    }

    setFullPaying(true);
    try {
      let created: any = null;
      if (selectedChargeIds.length > 0) {
        const response = await createInvoice.mutateAsync(buildInvoicePayload());
        created = (response as any)?.data || null;
      }

      const paymentsToMake: Array<{ invoiceId: string; amount: number }> = [];
      const newInvoiceDue = Number(created?.dueAmount ?? created?.total ?? 0);
      if (created?.id && newInvoiceDue > 0) {
        paymentsToMake.push({ invoiceId: created.id, amount: newInvoiceDue });
      }
      for (const invoice of openInvoices) {
        paymentsToMake.push({
          invoiceId: invoice.id,
          amount: Number(invoice.dueAmount),
        });
      }

      // Post one payment at a time: the cashbook balance is derived from the
      // previous entry, so concurrent payments for the same visit would race.
      for (const payment of paymentsToMake) {
        if (!(payment.amount > 0)) continue;
        await recordPayment.mutateAsync({
          invoiceId: payment.invoiceId,
          amount: payment.amount,
          paymentMode,
          referenceNumber: paymentReference.trim() || undefined,
          receivedBy: user?.id,
        });
      }

      if (created) {
        const totalNow = Number(created.total || 0);
        setCreatedInvoice({ ...created, paidAmount: totalNow, dueAmount: 0 });
      } else {
        setCreatedInvoice(null);
      }
      setSettledEarlierCount(openInvoices.length);

      // Re-read the live bill so the success screen reflects every invoice,
      // not just the ones this dialog happened to touch.
      let cleared = true;
      let carried = 0;
      try {
        const fresh = await ipdService.getAdmissionBillingStatus(admission.id);
        cleared = Boolean(fresh.data?.isCleared);
        carried = fresh.data?.canDischarge
          ? Number(fresh.data?.outstandingDue) || 0
          : 0;
      } catch {
        // Fall back to the optimistic result; staff can still retry.
      }
      setBillCleared(cleared);
      setCarriedDue(carried);
      setStep('invoice-success');
    } catch (settleError: any) {
      const message =
        settleError?.message || 'Could not settle the bill. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setFullPaying(false);
    }
  };

  const handlePrint = async () => {
    const id = createdInvoice?.id;
    if (!id) return;
    setPrinting(true);
    try {
      const url = await billingService.fetchInvoicePdfUrl(id);
      await printInvoicePdf(url);
    } catch (printError: any) {
      toast.error(printError?.message || 'Failed to print the invoice.');
    } finally {
      setPrinting(false);
    }
  };

  const handleDownload = () => {
    const id = createdInvoice?.id;
    if (!id) return;
    setDownloading(true);
    try {
      // The endpoint responds with Content-Disposition: attachment, so a native
      // anchor click downloads the PDF without a cross-origin XHR read.
      const url = billingService.getInvoicePdfUrl(id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${createdInvoice.invoiceNumber || id}.pdf`;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice PDF downloaded');
    } catch (downloadError: any) {
      toast.error(downloadError?.message || 'Failed to download the invoice.');
    } finally {
      setDownloading(false);
    }
  };

  if (!open || !admission) return null;

  const busy =
    createInvoice.isPending ||
    recordPayment.isPending ||
    fullPaying ||
    discharging ||
    savingChargeId !== null;

  const modal = (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-md sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admission-invoice-title"
      onClick={() => {
        if (!busy) onDone();
      }}
    >
      <div
        className={`flex max-h-[94vh] w-full flex-col overflow-hidden rounded-3xl border border-border/70 bg-surface shadow-[0_24px_80px_-20px_rgba(15,23,42,0.45)] ${
          step === 'invoice' ? 'max-w-5xl' : 'max-w-2xl'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="relative shrink-0 overflow-hidden border-b border-border/70 bg-gradient-to-br from-primary/15 via-surface to-secondary/10 px-5 py-4 sm:px-6">
          <div className="pointer-events-none absolute -right-10 -top-14 h-36 w-36 rounded-full bg-primary/10 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-lg shadow-primary/25">
                {step === 'invoice-success' ? <BadgeCheck size={21} /> : <Sparkles size={21} />}
              </span>
              <div className="min-w-0">
                <p className="text-[0.625rem] font-semibold uppercase tracking-[0.18em] text-primary">
                  {step === 'invoice-success'
                    ? billCleared
                      ? 'Bill cleared'
                      : isExisting
                        ? 'Invoice created'
                        : 'Admission complete'
                    : isExisting
                      ? 'Admission billing'
                      : 'Admission created'}
                </p>
                <h2
                  id="admission-invoice-title"
                  className="truncate text-lg font-bold leading-tight text-text sm:text-xl"
                >
                  {step === 'invoice-success'
                    ? billCleared
                      ? 'Ready to discharge'
                      : 'Invoice created'
                    : 'Quick invoice'}
                </h2>
                <p className="truncate text-xs text-text-muted">
                  {patientName || 'Admitted patient'}
                  {admission.bed?.bedNumber ? ` · Bed ${admission.bed.bedNumber}` : ''}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onDone}
              disabled={busy}
              className="rounded-xl border border-border/70 bg-surface/80 p-2 text-text-muted transition hover:bg-background hover:text-text disabled:opacity-50"
              aria-label="Close"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {error && (
            <div className="mx-5 mt-4 flex items-start gap-2 rounded-xl border border-danger/20 bg-danger/10 p-3 text-sm text-danger sm:mx-6">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* ── Invoice ── */}
          {step === 'invoice' && (
            <div className="grid grid-cols-1 gap-5 p-5 sm:p-6 lg:grid-cols-[1.15fr_0.85fr]">
              {/* Charges */}
              <section className="min-w-0 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="flex items-center gap-1.5 text-sm font-bold text-text">
                      <FileText size={15} className="text-primary" />
                      Unbilled charges
                    </h3>
                    <p className="text-[0.6875rem] text-text-muted">
                      Select the services to include on this invoice.
                    </p>
                  </div>
                  {unbilledCharges.length > 0 && (
                    <button
                      type="button"
                      onClick={toggleAll}
                      className="shrink-0 rounded-lg border border-border bg-surface px-2.5 py-1 text-[0.6875rem] font-semibold text-text-muted transition hover:border-primary/30 hover:text-primary"
                    >
                      {allSelected ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>

                {chargesLoading ? (
                  <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-background/50 py-10 text-sm text-text-muted">
                    <Loader2 size={16} className="animate-spin text-primary" />
                    Loading charges…
                  </div>
                ) : unbilledCharges.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-background/50 px-4 py-10 text-center">
                    <Receipt size={22} className="text-text-muted" />
                    <p className="text-sm font-semibold text-text">No unbilled charges</p>
                    <p className="max-w-xs text-xs text-text-muted">
                      This visit has nothing left to invoice. Bed charges appear here after the
                      first night.
                    </p>
                  </div>
                ) : (
                  <div className="max-h-[330px] space-y-2 overflow-y-auto pr-0.5">
                    {unbilledCharges.map((charge: any) => {
                      const checked = selectedChargeIds.includes(charge.id);
                      const amount = Number(charge.amount || 0);
                      const tax = Number(charge.tax || 0);
                      // Bed nights are posted automatically from the bed rate
                      // and the stay length, so their quantity is never editable.
                      const canChangeQuantity =
                        canEditChargeQuantity &&
                        charge.type !== 'bed_allocation' &&
                        Boolean(charge.service);
                      return (
                        <div
                          key={charge.id}
                          className={`flex items-center gap-2 rounded-xl border p-3 transition ${
                            checked
                              ? 'border-primary/40 bg-primary/5 shadow-sm'
                              : 'border-border bg-surface hover:border-primary/25 hover:bg-background/60'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleCharge(charge.id)}
                            className="flex min-w-0 flex-1 items-start gap-3 text-left"
                          >
                            <span
                              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] border transition ${
                                checked
                                  ? 'border-primary bg-primary text-white'
                                  : 'border-border bg-surface'
                              }`}
                            >
                              {checked && <CheckCircle2 size={11} />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs font-semibold text-text">
                                {charge.chargeDescription || charge.service?.name || 'Service'}
                              </span>
                              <span className="mt-0.5 block text-[0.625rem] text-text-muted">
                                {charge.service?.code ? `${charge.service.code} · ` : ''}
                                {charge.type?.replace(/_/g, ' ').toLowerCase() || 'charge'}
                                {charge.quantity && Number(charge.quantity) > 1
                                  ? ` · ${charge.quantity} ${charge.unitLabel || 'units'}`
                                  : ''}
                              </span>
                            </span>
                            <span className="w-16 shrink-0 text-right tabular-nums">
                              <span className="block text-xs font-bold text-text">
                                {money(amount)}
                              </span>
                              {tax > 0 && (
                                <span className="block text-[0.625rem] text-text-muted">
                                  + {money(tax)} tax
                                </span>
                              )}
                            </span>
                          </button>

                          {canEditChargeQuantity && (
                            <div className="flex w-[128px] shrink-0 items-center justify-end gap-1.5">
                              {canChangeQuantity ? (
                                <>
                                  <span className="text-[0.625rem] font-semibold uppercase tracking-wide text-text-muted">
                                    Qty
                                  </span>
                                  <QuantityStepper
                                    value={
                                      quantityDrafts[charge.id] ??
                                      Number(charge.quantity || 1)
                                    }
                                    onChange={(next) =>
                                      handleChargeQuantityChange(charge, next)
                                    }
                                    min={serviceQuantityBounds(charge.service || {}).min}
                                    max={serviceQuantityBounds(charge.service || {}).max}
                                    step={serviceQuantityBounds(charge.service || {}).step}
                                    size="sm"
                                    disabled={savingChargeId === charge.id}
                                    ariaLabel={`${charge.chargeDescription || 'Charge'} quantity`}
                                  />
                                  {savingChargeId === charge.id && (
                                    <Loader2
                                      size={12}
                                      className="animate-spin text-primary"
                                    />
                                  )}
                                </>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Summary */}
              <section className="space-y-3">
                <div className="overflow-hidden rounded-2xl border border-border bg-background/50">
                  <div className="border-b border-border px-4 py-3">
                    <h3 className="text-sm font-bold text-text">Invoice summary</h3>
                    <p className="text-[0.6875rem] text-text-muted">
                      {totals.count} charge{totals.count === 1 ? '' : 's'} selected
                    </p>
                  </div>

                  <div className="space-y-2 px-4 py-3 text-xs">
                    <div className="flex items-center justify-between text-text-muted">
                      <span>Subtotal</span>
                      <span className="font-semibold text-text">{money(totals.subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between text-text-muted">
                      <span>Tax</span>
                      <span className="font-semibold text-text">{money(totals.tax)}</span>
                    </div>

                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between">
                        <span className="text-text-muted">Discount</span>
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={discountValue}
                            onChange={(event) => handleDiscountChange(event.target.value)}
                            placeholder="0"
                            className="w-16 rounded-lg border border-border bg-surface px-2 py-1 text-right text-xs text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                          <div className="flex overflow-hidden rounded-lg border border-border bg-surface">
                            {(['PERCENT', 'FLAT'] as const).map((type) => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => setDiscountType(type)}
                                className={`px-1.5 py-1 text-[0.625rem] font-bold transition ${
                                  discountType === type
                                    ? 'bg-primary text-white'
                                    : 'text-text-muted hover:text-text'
                                }`}
                              >
                                {type === 'PERCENT' ? '%' : '৳'}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      {totals.discount > 0 && (
                        <div className="flex items-center justify-between text-success">
                          <span>Discount applied</span>
                          <span className="font-semibold">− {money(totals.discount)}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-2 flex items-center justify-between rounded-xl bg-primary/10 px-3 py-2.5">
                      <span className="text-xs font-semibold text-primary">Total due</span>
                      <span className="text-base font-bold text-primary">{money(totals.total)}</span>
                    </div>
                  </div>
                </div>

                {isExisting && (
                  <div
                    className={`rounded-2xl border p-4 ${
                      earlierDue > 0
                        ? 'border-warning/30 bg-warning/5'
                        : 'border-border bg-surface'
                    }`}
                  >
                    <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-text">
                      <ReceiptText size={14} className="text-primary" />
                      Earlier invoices
                    </h4>
                    {billingStatusLoading ? (
                      <p className="flex items-center gap-1.5 text-xs text-text-muted">
                        <Loader2 size={12} className="animate-spin text-primary" />
                        Checking the bill…
                      </p>
                    ) : earlierDue > 0 ? (
                      <>
                        <ul className="space-y-1.5 text-xs">
                          {openInvoices.map((invoice) => (
                            <li
                              key={invoice.id}
                              className="flex items-center justify-between gap-2"
                            >
                              <span className="truncate text-text-muted">
                                {invoice.invoiceNumber || invoice.id.slice(0, 8)}
                              </span>
                              <span className="shrink-0 font-semibold text-danger">
                                {money(Number(invoice.dueAmount))}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-xs">
                          <span className="font-semibold text-text">Outstanding</span>
                          <span className="font-bold text-warning">{money(earlierDue)}</span>
                        </div>
                        <p className="mt-2 text-[0.6875rem] leading-relaxed text-text-muted">
                          <span className="font-semibold text-text">Full pay all</span> settles
                          these invoices together with the new one.
                        </p>
                      </>
                    ) : (
                      <p className="text-xs font-medium text-success">
                        All earlier invoices are fully paid.
                      </p>
                    )}
                  </div>
                )}

                <div className="rounded-2xl border border-border bg-surface p-4">
                  <h4 className="mb-2.5 flex items-center justify-between gap-2 text-xs font-bold uppercase tracking-wide text-text">
                    <span className="flex items-center gap-1.5">
                      <Banknote size={14} className="text-primary" />
                      Collect payment now
                      <span className="font-normal normal-case text-text-muted">(optional)</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setPaymentAmount(String(totals.total))}
                      disabled={totals.total <= 0}
                      className="shrink-0 text-[0.625rem] font-bold uppercase tracking-wide text-primary hover:underline disabled:no-underline disabled:opacity-50"
                      title="Fill the payment amount with this invoice's total"
                    >
                      Autofill {money(totals.total)}
                    </button>
                  </h4>

                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={paymentAmount}
                        onChange={(event) => setPaymentAmount(sanitizeDecimalInput(event.target.value))}
                        placeholder="0"
                        disabled={totals.total <= 0}
                        className={`${inputClass} text-right font-semibold disabled:opacity-50`}
                      />
                    </div>

                    <div className="grid grid-cols-4 gap-1.5">
                      {PAYMENT_MODES.map((mode) => {
                        const Icon = mode.icon;
                        const active = paymentMode === mode.value;
                        return (
                          <button
                            key={mode.value}
                            type="button"
                            onClick={() => setPaymentMode(mode.value)}
                            className={`flex flex-col items-center gap-1 rounded-xl border px-1 py-2 text-[0.625rem] font-semibold transition ${
                              active
                                ? 'border-primary/40 bg-primary/10 text-primary'
                                : 'border-border bg-surface text-text-muted hover:border-primary/25'
                            }`}
                          >
                            <Icon size={14} />
                            {mode.label}
                          </button>
                        );
                      })}
                    </div>

                    {paymentMode !== 'cash' && (
                      <input
                        type="text"
                        value={paymentReference}
                        onChange={(event) => setPaymentReference(event.target.value)}
                        placeholder="Reference / transaction ID"
                        className={inputClass}
                      />
                    )}

                    {paymentValue > 0 && (
                      <p className="rounded-lg bg-success/10 px-2.5 py-1.5 text-[0.6875rem] text-success">
                        Collecting {money(paymentValue)} ·{' '}
                        {money(Math.max(totals.total - paymentValue, 0))} will remain due
                      </p>
                    )}
                  </div>
                </div>

                {/* Invoice date & time — the bill's business date. An IPD bill
                    defaults to the stay's admission day. */}
                {canBackdateInvoice && (
                  <div>
                    <label className="mb-1 flex items-center gap-1 text-[0.625rem] font-bold uppercase tracking-wide text-text-muted">
                      Invoice Date &amp; Time
                      <InfoHint
                        label="Invoice Date & Time"
                        en="The date this bill is posted on (printed on the bill, used by the date filters, reports and ledger). Defaults to the admission date."
                        bn="এই বিল যে তারিখে পোস্ট হয়েছে (বিলে মুদ্রিত, ডেট ফিল্টার/রিপোর্ট/লেজারে ব্যবহৃত)। ডিফল্ট: ভর্তির তারিখ।"
                      />
                    </label>
                    <DateTimePicker
                      value={invoiceDate}
                      onChange={(value) => {
                        setInvoiceDate(value);
                        if (value) setInvoiceDateError(undefined);
                      }}
                      error={invoiceDateError}
                      timeZone={hospitalTimezone}
                      required
                    />
                    <p className="mt-1 text-[0.625rem] leading-4 text-text-muted">
                      Times in {hospitalTimezone} (Settings → General)
                    </p>
                  </div>
                )}

                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Invoice notes (optional)"
                  rows={2}
                  className={`${inputClass} resize-none`}
                />
              </section>
            </div>
          )}

          {/* ── Invoice success ── */}
          {step === 'invoice-success' &&
            (createdInvoice || billCleared || settledEarlierCount > 0) && (
            <div className="flex flex-col items-center px-5 py-8 text-center sm:px-8">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success/10 text-success ring-8 ring-success/5">
                <BadgeCheck size={34} />
              </span>
              <h3 className="text-lg font-bold text-text">
                {billCleared ? 'Bill cleared successfully' : 'Invoice created successfully'}
              </h3>
              <p className="mt-1 text-sm text-text-muted">
                {createdInvoice ? (
                  <>
                    The admission charges are now on invoice{' '}
                    <span className="font-semibold text-text">
                      {createdInvoice.invoiceNumber || createdInvoice.id?.slice(0, 8)}
                    </span>
                    .
                  </>
                ) : (
                  'Every open invoice for this admission has been settled.'
                )}
              </p>

              {createdInvoice && (
                <div className="mt-5 w-full max-w-sm divide-y divide-border overflow-hidden rounded-2xl border border-border bg-background/50 text-sm">
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-text-muted">Invoice total</span>
                    <span className="font-bold text-text">
                      {money(Number(createdInvoice.total || 0))}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-text-muted">Paid now</span>
                    <span className="font-semibold text-success">
                      {money(Number(createdInvoice.paidAmount || 0))}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-text-muted">Remaining due</span>
                    <span
                      className={`font-semibold ${
                        Number(createdInvoice.dueAmount || 0) > 0
                          ? 'text-danger'
                          : 'text-success'
                      }`}
                    >
                      {money(Number(createdInvoice.dueAmount || 0))}
                    </span>
                  </div>
                  {settledEarlierCount > 0 && (
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-text-muted">Earlier invoices settled</span>
                      <span className="font-semibold text-text">{settledEarlierCount}</span>
                    </div>
                  )}
                </div>
              )}

              {billCleared && (
                <p className="mt-4 w-full max-w-sm rounded-xl border border-success/20 bg-success/10 px-3 py-2.5 text-xs font-medium text-success">
                  Nothing is outstanding on this visit — the patient can be discharged now.
                </p>
              )}

              {!billCleared && carriedDue > 0 && (
                <p className="mt-4 w-full max-w-sm rounded-xl border border-warning/25 bg-warning/10 px-3 py-2.5 text-xs font-medium text-warning">
                  {money(carriedDue)} will remain due. You can discharge now and collect
                  the balance later from Billing → Invoices.
                </p>
              )}

              <div className="mt-6 w-full max-w-md space-y-2">
                {createdInvoice && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePrint}
                      isLoading={printing}
                      disabled={printing || downloading}
                    >
                      <Printer size={15} className="mr-1.5" />
                      Print
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDownload}
                      isLoading={downloading}
                      disabled={printing || downloading}
                    >
                      <Download size={15} className="mr-1.5" />
                      Download PDF
                    </Button>
                  </div>
                )}

                {isExisting ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={onGoToList || onDone}
                      disabled={discharging}
                    >
                      <ListChecks size={15} className="mr-1.5" />
                      Go to List
                    </Button>
                    {canDischargeNow && onDischarge && (
                      <Button
                        type="button"
                        variant="primary"
                        className="w-full"
                        onClick={onDischarge}
                        isLoading={discharging}
                        disabled={discharging}
                      >
                        <CheckCircle2 size={15} className="mr-1.5" />
                        {billCleared
                          ? 'Discharge'
                          : `Discharge · ${money(carriedDue)} due`}
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    className="w-full"
                    onClick={onViewAdmission}
                  >
                    View admission
                    <ChevronRight size={15} className="ml-1" />
                  </Button>
                )}
              </div>
              <button
                type="button"
                onClick={onViewInvoices}
                disabled={discharging}
                className="mt-3 text-xs font-semibold text-primary hover:underline disabled:opacity-50"
              >
                Open invoices list
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'invoice' && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-background/40 px-5 py-3.5 sm:px-6">
            <button
              type="button"
              onClick={onDone}
              disabled={busy}
              className="inline-flex items-center gap-1 text-xs font-semibold text-text-muted transition hover:text-text disabled:opacity-50"
            >
              <ArrowLeft size={14} />
              Skip for now
            </button>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onDone} disabled={busy}>
                Close
              </Button>
              <Button
                type="button"
                variant={isExisting ? 'outline' : 'primary'}
                onClick={handleCreateInvoice}
                isLoading={createInvoice.isPending || (!isExisting && recordPayment.isPending)}
                disabled={busy || selectedChargeIds.length === 0 || chargesLoading}
              >
                <Receipt size={15} className="mr-1.5" />
                Create invoice · {money(totals.total)}
              </Button>
              {isExisting && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleFullPayAll}
                  isLoading={fullPaying}
                  disabled={busy || !canFullPayAll}
                  title="Create this invoice and settle every open invoice in full"
                >
                  <Wallet size={15} className="mr-1.5" />
                  Full pay all · {money(fullPayTotal)}
                </Button>
              )}
              {isExisting &&
                onDischarge &&
                canDischargeWithDue &&
                earlierDue > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onDischarge}
                    isLoading={discharging}
                    disabled={busy || discharging}
                    title="Leave the balance on the invoices and collect it later"
                  >
                    <CheckCircle2 size={15} className="mr-1.5" />
                    Discharge · {money(earlierDue)} due
                  </Button>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
